import { useBeforeUnload, useLocation } from '@remix-run/react'
import { get as getIn } from 'lodash-es'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useControlField, type FormContextValue } from 'remix-validated-form'
import { aIsFormLocked, useAtom } from '~/atoms'
import { toast } from '~/components/ui'
import { useLocalStorage } from '~/hooks'
import { getObjectChanges, preprocessFormData } from '~/utils'

export default function useFormHelpers<T extends object>(
  formId: string,
  formContext: FormContextValue,
  backupKey?: string
) {
  const location = useLocation()
  const [isFormChanged, setIsFormChanged] = useState<boolean>(false)
  const [previousState, setPreviousState] = useState<T | null>(null)
  const [_, setIsFormLocked] = useAtom(aIsFormLocked)
  const [shouldCheck, setShouldCheck] = useState<boolean>(false)
  const backupRef = useRef<T | null>(null)
  const [backupData, __, _clearBackupData] = useLocalStorage<
    T | Record<never, never>
  >(
    backupKey || 'noop', // default key here because this isn't used outside of listing form currently
    {}
  )

  const [brokers] = useControlField('brokers', formId)
  const [images] = useControlField('images', formId)
  const [files] = useControlField('files', formId)

  const clearBackupData = useCallback(() => {
    backupRef.current = null
    _clearBackupData()
    if (backupKey) {
      try {
        localStorage.removeItem(backupKey!)
      } catch (_) {
        console.error('Error removing backup', backupKey)
      }
    }
  }, [])

  useEffect(() => {
    setTimeout(() => setShouldCheck(true), 1000)
  }, [])

  const maybeSaveBackup = useCallback(() => {
    try {
      if (backupKey) {
        if (backupRef.current) {
          // Using localstorage directly instead of hook which doesn't work here
          localStorage.setItem(backupKey, JSON.stringify(backupRef.current))
        } else {
          const item = localStorage.getItem(backupKey)
          if (item && item === '{}') {
            localStorage.removeItem(backupKey)
          }
        }
      }
    } catch (error) {
      console.error('Error handling draft data', error)
    }
  }, [])
  useEffect(() => () => maybeSaveBackup(), [])
  useBeforeUnload(maybeSaveBackup)

  useEffect(() => {
    const handleEnter = (event: any) => {
      if (
        event.keyCode === 13 &&
        (event.target.nodeName === 'INPUT' ||
          event.target.nodeName === 'SELECT')
      ) {
        event.preventDefault()
      }
    }
    document.addEventListener('keydown', handleEnter)
    return () => document.removeEventListener('keydown', handleEnter)
  }, [])

  useEffect(() => {
    if (Object.keys(formContext.fieldErrors).length > 0) {
      console.error(formId, 'errors', formContext.fieldErrors)
      toast({
        title: 'Form Errors',
        description: 'Please check form for required fields and errors',
        variant: 'destructive',
      })
    }
  }, [formContext.fieldErrors, formId])

  useEffect(() => {
    if (
      formContext.hasBeenSubmitted &&
      Object.keys(formContext.fieldErrors).length === 0
    ) {
      setPreviousState(null)
    }
  }, [formContext])

  const updateChanges = useCallback(() => {
    if (formContext.isSubmitting) {
      return
    }

    // Default values get loaded after first render
    if (typeof getIn(formContext.defaultValues!, 'created') === 'undefined') {
      return
    } else if (!shouldCheck) {
      return
    }

    // location, sale and lease spaces _id, modified, created

    const data = preprocessFormData(formContext.getValues())
    if (previousState === null) {
      setPreviousState(data as T)
    } else {
      const changes = getObjectChanges(previousState, data)
      if (Object.keys(changes).length > 0) {
        console.info('Changes', changes)
        setIsFormChanged(true)
        setIsFormLocked(true)
        if (backupKey) {
          backupRef.current = data as T
        }
      }
    }
  }, [formContext, previousState, backupKey, shouldCheck])

  useEffect(() => {
    updateChanges()
  }, [brokers, images, files])

  useEffect(() => {
    updateChanges()
  }, [updateChanges])

  function handleOnBeforeUnload() {
    if (isFormChanged) {
      return 'Are you sure you want to leave this page? Changes you have made will not be saved.'
    }
  }

  useEffect(() => {
    if (process.env.NODE_ENV === 'development' && import.meta.hot) {
      return
    }
    window.addEventListener('beforeunload', handleOnBeforeUnload)
  }, [isFormChanged])

  useEffect(() => {
    window.removeEventListener('beforeunload', handleOnBeforeUnload)
    setIsFormLocked(false)
  }, [location.pathname])

  return {
    isFormChanged,
    backupData,
    clearBackupData,
  }
}
